// @noflow

import { forEach } from 'lodash';
import moment from 'moment';
import BracketMapping from './BracketMapping';

const initialState = {
  bracketLocked: true,
  submissionsOpen: false,
  deadline: 0,
  submissionOpenTime: 0,
  leaderboard: {
    fetching: false,
    error: false,
    leaders: [],
    totalBrackets: 0,
  },
  tournament: {
    fetching: false,
    error: false,
    games: [],
    raw: [],
  },
  teams: {},
  userPoints: {
    userName: '',
    rank: 0,
    points: 0,
    possiblePoints: 0,
  },
  userBracket: {
    fetching: false,
    error: false,
    picks: [],
    finalScore: 0,
    score: undefined,
    position: -1,
    contesetSubmitStart: '',
    contestSubmitEnd: '',
    contest_submit_end: '',
    contest_submit_start: '',
    bracketStartType: undefined,
    tiebreakerTotal: undefined,
    isComplete: undefined,
    hasConflicts: undefined,
    lastSubmitted: undefined,
    numUpdates: undefined,
    requestCount: undefined,
    receivedPicks: [],
    hasAcceptedTerms: undefined,
  },
  userPicksCompleted: false,
  winners: [],
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case 'REQUEST_SPORTSBRACKETS_LEADERBOARD':
      return {
        ...state,
        leaderboard: {
          ...state.leaderboard,
          fetching: true,
        },
        uuid: action.uuid,
      };

    case 'RECEIVE_SPORTSBRACKETS_LEADERBOARD':
      return {
        ...state,
        leaderboard: {
          ...state.leaderboard,
          fetching: false,
          leaders: action.payload,
        },
      };

    case 'REQUEST_SPORTSBRACKETS_LEADERBOARD_ERROR':
      return {
        ...state,
        leaderboard: {
          ...state.leaderboard,
          fetching: false,
          error: true,
        },
      };
    case 'RECEIVE_SPORTSBRACKETS_DEADLINE': {
      const deadline = action.payload;
      const bracketLocked = moment().isAfter(deadline);

      return {
        ...state,
        fetching: false,
        bracketLocked,
        deadline,
      };
    }

    case 'RECEIVE_SPORTSBRACKETS_SUBMISSION_OPEN_TIME': {
      const submissionOpenTime = action.payload;
      const submissionsOpen = moment().isAfter(submissionOpenTime);

      return {
        ...state,
        fetching: false,
        submissionsOpen,
        submissionOpenTime,
      };
    }

    case 'REQUEST_SPORTSBRACKETS_TOURNAMENT':
      return {
        ...state,
        tournament: {
          ...state.tournament,
          fetching: true,
          error: false,
        },
      };

    case 'RECEIVE_SPORTSBRACKETS_TOURNAMENT': {
      const tournament = action.payload.slice();
      const startingTeams = tournament.filter(game => game.round === 1);
      const { userBracket } = state;
      const teams = {};

      forEach(startingTeams, game => {
        teams[game.upperTeam.echoTeamId] = { ...game.upperTeam, score: 0 };
        teams[game.lowerTeam.echoTeamId] = { ...game.lowerTeam, score: 0 };
      });

      forEach(BracketMapping, ([upper, lower], slotId) => {
        const game = tournament.find(game => game.slotNumber === Number(slotId));

        if (game) {
          const userUpperPick = userBracket.picks[upper - 1];
          const userLowerPick = userBracket.picks[lower - 1];
          if (game.upperTeam === null || game.upperTeam === undefined) {
            game.upperTeam = {};
          }

          if (game.lowerTeam === null || game.lowerTeam === undefined) {
            game.lowerTeam = {};
          }

          if (
            userUpperPick &&
            game.upperTeam.echoTeamId !== userUpperPick &&
            teams[userUpperPick]
          ) {
            game.upperTeam = teams[userUpperPick];
          }

          if (
            userLowerPick &&
            game.lowerTeam.echoTeamId !== userLowerPick &&
            teams[userLowerPick]
          ) {
            game.lowerTeam = teams[userLowerPick];
          }
        }
      });

      return {
        ...state,
        tournament: {
          ...state.tournament,
          raw: action.raw,
          games: tournament,
          fetching: false,
          error: false,
        },
      };
    }

    case 'REQUEST_SPORTSBRACKETS_TOURNAMENT_ERROR':
      return {
        ...state,
        tournament: {
          ...state.tournament,
          fetching: false,
          error: true,
        },
      };

    case 'REQUEST_SEND_PICKS':
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          fetching: true,
          error: false,
        },
      };

    case 'RECEIVE_SEND_PICKS': {
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          fetching: false,
          error: false,
        },
      };
    }

    case 'REQUEST_SEND_PICKS_ERROR':
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          fetching: false,
          error: true,
        },
      };

    case 'RECEIVE_USER_POINTS':
      return {
        ...state,
        userPoints: action.payload || state.userPoints,
        fetching: false,
        error: false,
      };

    case 'UPDATE_PICKS': {
      const userPicks = [...state.userBracket.picks];
      const teamId = action.team.echoTeamId;
      const games = state.tournament.games.slice();

      const oldPick = userPicks[action.slotId - 1];
      if (oldPick) {
        forEach(userPicks, (teamId, i) => {
          if (teamId === oldPick && i >= action.slotId) {
            userPicks[i] = undefined;
          }
        });
        forEach(games, (game, i) => {
          if (game.upperTeam.echoTeamId === oldPick && i >= action.slotId) {
            games[i].upperTeam = {
              echoTeamId: '0',
              city: '',
            };
          }
          if (game.lowerTeam.echoTeamId === oldPick && i >= action.slotId) {
            games[i].lowerTeam = {
              echoTeamId: '0',
              city: '',
            };
          }
        });
      }

      userPicks[action.slotId - 1] = teamId;

      forEach(games, game => {
        if (game.gameStatus === 'PLACEHOLDER' && game.slot > action.slotId) {
          if (game.upperTeam && game.upperTeam.echoTeamId === oldPick) {
            game.upperTeam = {};
          }
          if (game.lowerTeam && game.lowerTeam.echoTeamId === oldPick) {
            game.lowerTeam = {};
          }
        }
      });

      forEach(BracketMapping, ([upper, lower], slotId) => {
        if (upper === action.slotId) {
          const game = games.find(game => game.slotNumber === Number(slotId));
          if (game) {
            games[slotId - 1].upperTeam = action.team;
          }
        } else if (lower === action.slotId) {
          const game = games.find(game => game.slotNumber === Number(slotId));

          if (game) {
            games[slotId - 1].lowerTeam = action.team;
          }
        }
      });

      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          picks: userPicks,
        },
        userPicksCompleted: userPicks.length === 63 && userPicks.every(pick => pick),
        tournament: {
          ...state.tournament,
          games,
        },
      };
    }

    case 'UPDATE_FINAL_SCORE': {
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          finalScore: action.score,
          fetching: false,
          error: false,
        },
      };
    }
    case 'ACCEPT_BRACKET_TERMS': {
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          hasAcceptedTerms: true,
        },
      };
    }
    case 'REJECT_BRACKET_TERMS': {
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          hasAcceptedTerms: false,
        },
      };
    }
    case 'REQUEST_USER_PICKS': {
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          fetching: true,
          error: false,
        },
      };
    }

    case 'RECEIVE_USER_PICKS': {
      const {
        score,
        position,
        contestSubmitStart,
        contestSubmitEnd,
        // eslint-disable-next-line camelcase
        contest_submit_end,
        // eslint-disable-next-line camelcase
        contest_submit_start,
        bracketStartType,
        tiebreakerTotal,
        isComplete,
        hasConflicts,
        lastSubmitted,
        numUpdates,
        requestCount,
        bracketData,
        hasAcceptedTerms,
      } = action.payload;

      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          score,
          finalScore: tiebreakerTotal,
          position,
          contestSubmitStart,
          contestSubmitEnd,
          contest_submit_end,
          contest_submit_start,
          bracketStartType,
          tiebreakerTotal,
          hasAcceptedTerms,
          isComplete,
          hasConflicts,
          lastSubmitted,
          numUpdates,
          requestCount,
          receivedPicks: bracketData,
          picks: bracketData.map(team => team?.echoTeamId) || [],
          fetching: false,
          error: false,
        },
      };
    }

    case 'REQUEST_USER_PICKS_ERROR': {
      return {
        ...state,
        userBracket: {
          ...state.userBracket,
          fetching: false,
          error: true,
        },
      };
    }

    case 'REQUEST_SPORTSBRACKETS_WINNERS': {
      return {
        ...state,
        fetching: true,
        error: false,
      };
    }

    case 'RECEIVE_SPORTSBRACKETS_WINNERS': {
      return {
        ...state,
        winners: action.payload.winners || [],
        fetching: false,
        error: false,
      };
    }

    case 'REQUEST_SPORTSBRACKETS_WINNERS_ERROR': {
      return {
        ...state,
        fetching: false,
        error: true,
      };
    }

    default:
      return state;
  }
};

export default reducer;
